// =============================================================================
// Minimal chart primitives for the SaaS-Admin console.
//
// Hand-rolled on purpose: the app has no charting dependency, and these two
// forms don't warrant adding one. Both draw from the console's own CSS custom
// properties so they stay in step with the theme.
//
// Colour: a single series in --lp-sky-blue-dark (#0088bc), which measures
// 4.00:1 against the white card surface — clear of the 3:1 floor for marks, so
// no fallback labelling is needed to make values readable. Axes and gridlines
// are recessive hairlines; text never wears the series colour.
//
// Both forms are single-series, so neither carries a legend — the card title
// names what is plotted. Values are directly labelled or shown on the axis, so
// the tooltips enhance rather than gate.
// =============================================================================
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

// ---- shared -----------------------------------------------------------------

export interface TrendPoint {
  /** Stable key (ISO day). */
  key: string;
  /** Short axis label. */
  label: string;
  /** Full label for the tooltip. */
  fullLabel: string;
  value: number;
  count: number;
}

export interface BarRow {
  key: string;
  label: string;
  sublabel?: string;
  value: number;
  count?: number;
}

/**
 * Picks a clean axis top and its ticks. Tries 4, 5 and 3 intervals and keeps the
 * one with the least dead headroom — a fixed interval count leaves a peak of
 * 137.5 sitting on a 200 axis, wasting a third of the plot.
 */
export function niceScale(dataMax: number): { max: number; ticks: number[] } {
  if (!isFinite(dataMax) || dataMax <= 0) return { max: 1, ticks: [0, 0.5, 1] };

  const STEPS = [1, 2, 2.5, 5, 10];
  let bestMax = Infinity;
  let bestCount = 4;

  for (const count of [4, 5, 3]) {
    const raw = dataMax / count;
    const base = Math.pow(10, Math.floor(Math.log10(raw)));
    const step = (STEPS.find(s => raw / base <= s) ?? 10) * base;
    const max = step * count;
    if (max < bestMax) { bestMax = max; bestCount = count; }
  }

  return {
    max: bestMax,
    ticks: Array.from({ length: bestCount + 1 }, (_, i) => (bestMax / bestCount) * i),
  };
}

function useMeasure<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (typeof w === 'number') setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

// ---- trend (area + line) ----------------------------------------------------

const PAD = { top: 16, right: 16, bottom: 28, left: 56 };

/**
 * Single-series area chart for a daily time series. A crosshair snaps to the
 * nearest day so the reader aims at a date rather than at a 2px line; arrow keys
 * do the same from the keyboard.
 */
export const TrendArea: React.FC<{
  data: TrendPoint[];
  height?: number;
  formatValue: (n: number) => string;
  valueLabel: string;
  countLabel: string;
}> = ({ data, height = 230, formatValue, valueLabel, countLabel }) => {
  const [wrapRef, width] = useMeasure<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = Math.max(0, height - PAD.top - PAD.bottom);

  const scale = niceScale(Math.max(...data.map(d => d.value), 0));
  const x = (i: number) =>
    PAD.left + (data.length <= 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
  const y = (v: number) =>
    PAD.top + plotH - (scale.max === 0 ? 0 : (v / scale.max) * plotH);

  const onMove = useCallback((e: React.PointerEvent<SVGRectElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const rel = e.clientX - box.left;
    const i = data.length <= 1 ? 0 : Math.round((rel / box.width) * (data.length - 1));
    setActive(Math.max(0, Math.min(data.length - 1, i)));
  }, [data.length]);

  const onKey = useCallback((e: React.KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setActive(prev => {
      const start = prev ?? (e.key === 'ArrowRight' ? -1 : data.length);
      return Math.max(0, Math.min(data.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
    });
  }, [data.length]);

  if (data.length === 0) {
    return <div className="viz-empty">No metered activity in this window.</div>;
  }

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`).join(' ');
  const areaPath = plotW > 0
    ? `${linePath} L${x(data.length - 1).toFixed(1)},${(PAD.top + plotH).toFixed(1)} L${x(0).toFixed(1)},${(PAD.top + plotH).toFixed(1)} Z`
    : '';

  const ticks = scale.ticks;

  // Label the peak only — a value on every point is noise, and the axis plus the
  // crosshair carry the rest.
  const peak = data.reduce((best, d, i) => (d.value > data[best].value ? i : best), 0);
  const showPeak = data[peak].value > 0;

  // ~6 x labels, always including the last day.
  const labelEvery = Math.max(1, Math.ceil(data.length / 6));

  const point = active === null ? null : data[active];

  // Place the readout so it never sits on top of the data it describes: flip to
  // whichever side of the crosshair has room, then drop it to the bottom of the
  // plot if the line runs high anywhere under its footprint.
  const TIP_W = 172;
  const TIP_H = 82;
  let tipLeft = 0;
  let tipAtBottom = false;
  if (point) {
    const px = x(active as number);
    tipLeft = px + 12 + TIP_W > PAD.left + plotW ? px - 12 - TIP_W : px + 12;
    tipLeft = Math.max(0, Math.min(tipLeft, Math.max(0, width - TIP_W)));

    let peakUnderTip = 0;
    data.forEach((d, i) => {
      const dx = x(i);
      if (dx >= tipLeft - 8 && dx <= tipLeft + TIP_W + 8) peakUnderTip = Math.max(peakUnderTip, d.value);
    });
    tipAtBottom = y(peakUnderTip) < PAD.top + TIP_H + 8;
  }

  return (
    <div className="viz-wrap" ref={wrapRef}>
      {width > 0 && (
        <svg
          className="viz-svg"
          width={width}
          height={height}
          role="img"
          tabIndex={0}
          aria-label={`${valueLabel} per day. Use arrow keys to step through days.`}
          onKeyDown={onKey}
          onBlur={() => setActive(null)}
        >
          {/* gridlines — solid hairlines, one step off the surface */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line
                x1={PAD.left} x2={PAD.left + plotW}
                y1={y(t)} y2={y(t)}
                stroke={i === 0 ? 'var(--lp-grey-300)' : 'var(--lp-grey-200)'}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8} y={y(t) + 3}
                textAnchor="end" className="viz-axis-text"
              >
                {formatValue(t)}
              </text>
            </g>
          ))}

          {/* x labels */}
          {data.map((d, i) => (
            (i % labelEvery === 0 || i === data.length - 1) ? (
              <text key={d.key} x={x(i)} y={height - 8} textAnchor="middle" className="viz-axis-text">
                {d.label}
              </text>
            ) : null
          ))}

          {areaPath && <path d={areaPath} fill="var(--lp-sky-blue-dark)" fillOpacity={0.1} />}
          <path
            d={linePath}
            fill="none"
            stroke="var(--lp-sky-blue-dark)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* peak marker + its direct label */}
          {showPeak && (
            <>
              <circle
                cx={x(peak)} cy={y(data[peak].value)} r={4}
                fill="var(--lp-sky-blue-dark)" stroke="#fff" strokeWidth={2}
              />
              <text
                x={Math.max(PAD.left + 16, Math.min(x(peak), PAD.left + plotW - 16))}
                y={Math.max(12, y(data[peak].value) - 12)}
                textAnchor="middle" className="viz-peak-text"
              >
                {formatValue(data[peak].value)}
              </text>
            </>
          )}

          {/* crosshair */}
          {active !== null && (
            <g pointerEvents="none">
              <line
                x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + plotH}
                stroke="var(--lp-grey-400)" strokeWidth={1}
              />
              <circle
                cx={x(active)} cy={y(data[active].value)} r={4.5}
                fill="var(--lp-sky-blue-dark)" stroke="#fff" strokeWidth={2}
              />
            </g>
          )}

          {/* hit layer — the whole plot, so the pointer never has to find the line */}
          <rect
            x={PAD.left} y={PAD.top} width={plotW} height={plotH}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setActive(null)}
          />
        </svg>
      )}

      {point && width > 0 && (
        <div
          className="viz-tip"
          style={{
            left: tipLeft,
            width: TIP_W,
            ...(tipAtBottom ? { bottom: PAD.bottom + 6 } : { top: PAD.top }),
          }}
        >
          <div className="viz-tip__head">{point.fullLabel}</div>
          <div className="viz-tip__row">
            <span className="viz-tip__key" />
            <span className="viz-tip__val">{formatValue(point.value)}</span>
            <span className="viz-tip__lbl">{valueLabel}</span>
          </div>
          <div className="viz-tip__row">
            <span className="viz-tip__key viz-tip__key--muted" />
            <span className="viz-tip__val">{point.count.toLocaleString('en-US')}</span>
            <span className="viz-tip__lbl">{countLabel}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- ranked horizontal bars -------------------------------------------------

/** Space held clear at the right of the track so a tip value can never overflow. */
const VALUE_GUTTER = 76;

/**
 * Nominal categories compared by magnitude, so every bar wears the same hue —
 * shading them by value would re-encode what bar length already shows. Built in
 * HTML rather than SVG so long category names ellipsis cleanly and the rows are
 * focusable without extra work.
 *
 * No hover tooltip: the value rides the bar tip and the run count has its own
 * column, so both numbers are on screen permanently. A tooltip here would have
 * had to overlay the neighbouring row to appear at all.
 */
export const RankedBars: React.FC<{
  rows: BarRow[];
  formatValue: (n: number) => string;
  countLabel: string;
  emptyText?: string;
}> = ({ rows, formatValue, countLabel, emptyText = 'Nothing recorded in this window.' }) => {
  if (rows.length === 0) return <div className="viz-empty">{emptyText}</div>;

  const max = Math.max(...rows.map(r => r.value), 0);

  return (
    <ul className="viz-bars">
      {rows.map(r => {
        const pct = max <= 0 ? 0 : (r.value / max) * 100;
        // A non-zero value always gets a visible sliver; min() keeps the longest
        // bar clear of its own label.
        const width = `min(${Math.max(pct, r.value > 0 ? 1.5 : 0)}%, calc(100% - ${VALUE_GUTTER}px))`;

        return (
          <li className="viz-bar" key={r.key} tabIndex={0}>
            <span className="viz-bar__label" title={r.sublabel ? `${r.label} · ${r.sublabel}` : r.label}>
              {r.label}
              {r.sublabel && <em>{r.sublabel}</em>}
            </span>

            <span className="viz-bar__track">
              <span className="viz-bar__fill" style={{ width }} />
              <span className="viz-bar__value" style={{ left: width }}>{formatValue(r.value)}</span>
            </span>

            <span className="viz-bar__count">
              {typeof r.count === 'number' ? `${r.count.toLocaleString('en-US')} ${countLabel}` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
